import { DefaultButton, PrimaryButton, Stack, TextField } from '@fluentui/react';
import React, { useState } from 'react';
import { uniqueId, validateRequiredTextField } from 'src/utils';
import { localize } from 'src/utils/l10n';
import FormControl from '../FormControl/FormControl';
import FormSection from '../FormSection/FormSection';
import PopupHeader from '../PopupHeader/PopupHeader';
import CompactTable from '../CompactTable/CompactTable';
import UnstyledButton from '../UnstyledButton/UnstyledButton';
import Visage2Icon from '../Visage2Icon/Visage2Icon';
import Label from '../Label/Label';
import QuestionForm from './QuestionForm';

interface IProps {
    vector?: any;
    isCreatingVector?: boolean;
    onSave: (vector) => void;
    onCancel: () => void;
    canAddVector: () => boolean;
    canEditVector: (vectorId: number) => boolean;
    canAddQuestion: (vectorId: number) => boolean;
    canEditQuestion: (questionId: number) => boolean;
}

interface IState {
    vector: any;
    isCreatingQuestion?: boolean;
    question?: any;
}

const VectorForm = (props: IProps) => {
    const [state, setState] = useState<IState>({
        vector: props.vector ?
            {
                ...props.vector,
                questions: (props.vector.questions || []).map((q) => ({...q, tmpId: uniqueId()}))
            } :
            {
                questions: [],
                questionStatus: 1
            }
    });

    const removeItemAtIndex = (items: any[], index: number) => {
        const result = [...items];
    
        result.splice(index, 1);
    
        return result;
    }

    if (!!state.question) {
        return (
            <QuestionForm
                question={state.question}
                onSave={(item) => {
                    if (state.question.isNew) {
                        setState((s: IState) => ({
                            ...s,
                            question: undefined,
                            vector: {
                                ...s.vector,
                                questions: [...s.vector.questions, {...item, isNew: false}]
                            }
                        }));
                    } else {
                        setState((s: IState) => ({
                            ...s,
                            question: undefined,
                            vector: {
                                ...s.vector,
                                questions: s.vector.questions.map((q) => {
                                    if (q.tmpId === state.question.tmpId) {
                                        return item;
                                    }

                                    return q;
                                })
                            }
                        }));
                    }
                }}
                onCancel={() => {
                    setState((s: IState) => ({
                        ...s,
                        question: undefined
                    }));
                }}
            />
        )
    }

    return (
        <div>
            <PopupHeader
                text={!props.canEditVector(state.vector.id) ?
                    state.vector.name :
                    props.isCreatingVector ?
                        localize("CREATE_VECTOR") :
                        localize("EDIT_VECTOR")}
                hideCloseButton />
            {props.canEditVector(state.vector.id) && (
                <FormSection>
                    <FormControl>
                        <TextField
                            value={state.vector.name}
                            className="textField"
                            label={localize("Namn")}
                            resizable={false}
                            onChange={(ev?: React.FormEvent<HTMLElement | HTMLInputElement>, newValue?: string) => {
                                setState(s => ({
                                    ...s,
                                    vector: {
                                        ...s.vector,
                                        name: newValue
                                    }
                                }));
                            }}
                            required
                            aria-required
                            validateOnFocusOut
                            validateOnLoad={false}
                            onGetErrorMessage={validateRequiredTextField}
                            autoFocus
                        />
                    </FormControl>
                </FormSection>
            )}
            {state.vector.questions.length > 0 && (
                <CompactTable
                    columNames={[localize("QUESTION")]}
                    rows={state.vector.questions.map((item: any, index: number) => { return [item.text]; })}
                    buttons={state.vector.questions.map((item: any, index: number) => {
                        if (!props.canEditQuestion(item.id)) {
                            return [];
                        }

                        return [{
                            title: localize("Redigera"),
                            icon: "edit-2",
                            onClick: () => {
                                setState(s => ({
                                    ...s,
                                    question: item
                                }));
                            }
                        }, {
                            title: localize("TaBort"),
                            icon: "trash",
                            onClick: () => {
                                setState(s => ({
                                    ...s,
                                    vector: {
                                        ...s.vector,
                                        questions: removeItemAtIndex(state.vector.questions, index)
                                    }
                                }))
                            }
                        }];
                    })}
                    multiRow
                />
            )}
            <div className="table">
                {state.vector.questions.length === 0 && (
                    <Label size="body-2" color="p-mid-grey-5" className="no-questions">{localize("NO_QUESTIONS_GENERATE_ERROR")}</Label>
                )}
                {props.canAddQuestion(state.vector.id) && (
                    <div className="table-footer">
                        <UnstyledButton className="add-button" onClick={() => {
                            setState(s => ({
                                ...s,
                                question: {
                                    id: 0,
                                    tmpId: uniqueId(),
                                    text: "",
                                    isNew: true,
                                    questionAlternativeCollectionId: 0
                                }
                            }));
                        }}>
                            <Visage2Icon icon="add" color="p-green-1" />
                            <Label
                                size="body-2"
                                weight="medium"
                                color="p-green-1"
                                fontType="display"
                            >
                                {localize("ADD_QUESTION")}
                            </Label>
                        </UnstyledButton>
                    </div>
                )}
            </div>
            <Stack className="bottom-buttons" horizontal={true} horizontalAlign="end" tokens={{ childrenGap: 20 }}>
                <DefaultButton
                    onClick={() => {
                        props.onCancel();
                    }}
                    text={localize("GO_BACK")} />
                {(props.canEditVector(state.vector.id) || props.canAddQuestion(state.vector.id)) && (
                    <PrimaryButton
                        onClick={() => {
                            props.onSave(state.vector);
                        }}
                        text={localize("SAVE")} />
                )}
            </Stack>
        </div>
    )
}

export default VectorForm;